import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import type {
  Event,
  Participant,
  Group,
  Expense,
  CurrencyCode,
} from "../types";
import { loadEvents, saveEvents } from "../lib/storage";
import { generateId } from "../lib/id";
import { haptics } from "../lib/haptics";
import type { SaveState } from "../components/shared/SaveStatus";

interface EventContextValue {
  events: Event[];
  currentEvent: Event | null;
  currentEventId: string | null;
  isLoading: boolean;
  saveStatus: SaveState;

  // Event operations
  createEvent: (name: string, currency: CurrencyCode) => Event;
  updateEvent: (
    id: string,
    data: Partial<Pick<Event, "name" | "currency">>
  ) => void;
  deleteEvent: (id: string) => void;
  setCurrentEventId: (id: string | null) => void;

  // Participant operations
  addParticipant: (name: string) => Participant | null;
  updateParticipant: (
    participantId: string,
    data: Partial<Pick<Participant, "name">>
  ) => void;
  deleteParticipant: (participantId: string) => void;

  // Group operations
  createGroup: (
    name: string,
    participantIds: string[],
    representativeId: string
  ) => Group | null;
  updateGroup: (
    groupId: string,
    data: Partial<Pick<Group, "name" | "representativeId" | "participantIds">>
  ) => void;
  deleteGroup: (groupId: string) => void;
  assignParticipantToGroup: (participantId: string, groupId: string) => void;

  // Expense operations
  addExpense: (
    description: string,
    amount: number,
    paidById: string,
    participantIds: string[]
  ) => Expense | null;
  updateExpense: (
    expenseId: string,
    data: Partial<
      Pick<Expense, "description" | "amount" | "paidById" | "participantIds">
    >
  ) => void;
  deleteExpense: (expenseId: string) => void;
}

const EventContext = createContext<EventContextValue | null>(null);

export function EventProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<Event[]>([]);
  const [currentEventId, setCurrentEventId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveState>("idle");
  const isInitialLoad = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load events from localStorage on mount
  useEffect(() => {
    const loaded = loadEvents();
    setEvents(loaded);
    setIsLoading(false);
    isInitialLoad.current = false;
  }, []);

  // Save events to localStorage when they change
  useEffect(() => {
    // Skip saving on initial load
    if (isInitialLoad.current) return;

    // Clear any pending timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaveStatus("saving");
    const result = saveEvents(events);

    if (result.success) {
      setSaveStatus("saved");
      // Reset to idle after 2 seconds
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus("idle");
      }, 2000);
    } else {
      setSaveStatus("error");
      if (result.error) {
        toast.error(result.error);
      }
    }
  }, [events]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const currentEvent = events.find((e) => e.id === currentEventId) || null;

  // Helper to update the current event
  const updateCurrentEvent = useCallback(
    (updater: (event: Event) => Event) => {
      if (!currentEventId) return;
      setEvents((prev) =>
        prev.map((event) => {
          if (event.id === currentEventId) {
            return { ...updater(event), updatedAt: new Date().toISOString() };
          }
          return event;
        })
      );
    },
    [currentEventId]
  );

  // Event operations
  const createEvent = useCallback(
    (name: string, currency: CurrencyCode): Event => {
      const now = new Date().toISOString();
      const newEvent: Event = {
        id: generateId(),
        name,
        currency,
        createdAt: now,
        updatedAt: now,
        participants: [],
        groups: [],
        expenses: [],
      };
      setEvents((prev) => [...prev, newEvent]);
      return newEvent;
    },
    []
  );

  const updateEvent = useCallback(
    (id: string, data: Partial<Pick<Event, "name" | "currency">>) => {
      setEvents((prev) =>
        prev.map((event) =>
          event.id === id
            ? { ...event, ...data, updatedAt: new Date().toISOString() }
            : event
        )
      );
    },
    []
  );

  const deleteEvent = useCallback(
    (id: string) => {
      setEvents((prev) => prev.filter((event) => event.id !== id));
      if (currentEventId === id) {
        setCurrentEventId(null);
      }
    },
    [currentEventId]
  );

  // Participant operations
  const addParticipant = useCallback(
    (name: string): Participant | null => {
      if (!currentEventId) return null;

      const newParticipant: Participant = {
        id: generateId(),
        name,
        groupId: "",
      };

      updateCurrentEvent((event) => ({
        ...event,
        participants: [...event.participants, newParticipant],
      }));

      haptics.success();
      return newParticipant;
    },
    [currentEventId, updateCurrentEvent]
  );

  const updateParticipant = useCallback(
    (participantId: string, data: Partial<Pick<Participant, "name">>) => {
      updateCurrentEvent((event) => ({
        ...event,
        participants: event.participants.map((p) =>
          p.id === participantId ? { ...p, ...data } : p
        ),
      }));
    },
    [updateCurrentEvent]
  );

  const deleteParticipant = useCallback(
    (participantId: string) => {
      updateCurrentEvent((event) => {
        // Remove participant from groups
        const updatedGroups = event.groups.map((group) => ({
          ...group,
          participantIds: group.participantIds.filter(
            (id) => id !== participantId
          ),
          // If representative is deleted, clear it
          representativeId:
            group.representativeId === participantId
              ? ""
              : group.representativeId,
        }));

        // Remove expenses where this participant was the payer
        // And remove participant from expense splits
        const updatedExpenses = event.expenses
          .filter((expense) => expense.paidById !== participantId)
          .map((expense) => ({
            ...expense,
            participantIds: expense.participantIds.filter(
              (id) => id !== participantId
            ),
          }));

        return {
          ...event,
          participants: event.participants.filter(
            (p) => p.id !== participantId
          ),
          groups: updatedGroups,
          expenses: updatedExpenses,
        };
      });
    },
    [updateCurrentEvent]
  );

  // Group operations
  const createGroup = useCallback(
    (
      name: string,
      participantIds: string[],
      representativeId: string
    ): Group | null => {
      if (!currentEventId) return null;

      const newGroup: Group = {
        id: generateId(),
        name,
        participantIds,
        representativeId,
      };

      updateCurrentEvent((event) => {
        // Update participants to reference this group
        const updatedParticipants = event.participants.map((p) =>
          participantIds.includes(p.id) ? { ...p, groupId: newGroup.id } : p
        );

        return {
          ...event,
          groups: [...event.groups, newGroup],
          participants: updatedParticipants,
        };
      });

      haptics.success();
      return newGroup;
    },
    [currentEventId, updateCurrentEvent]
  );

  const updateGroup = useCallback(
    (
      groupId: string,
      data: Partial<Pick<Group, "name" | "representativeId" | "participantIds">>
    ) => {
      updateCurrentEvent((event) => {
        const updatedGroups = event.groups.map((g) =>
          g.id === groupId ? { ...g, ...data } : g
        );

        // If participantIds changed, update participant references
        let updatedParticipants = event.participants;
        if (data.participantIds) {
          const group = event.groups.find((g) => g.id === groupId);
          if (group) {
            // Remove old participants from this group
            updatedParticipants = updatedParticipants.map((p) =>
              p.groupId === groupId ? { ...p, groupId: "" } : p
            );
            // Add new participants to this group
            updatedParticipants = updatedParticipants.map((p) =>
              data.participantIds!.includes(p.id) ? { ...p, groupId } : p
            );
          }
        }

        return {
          ...event,
          groups: updatedGroups,
          participants: updatedParticipants,
        };
      });
    },
    [updateCurrentEvent]
  );

  const deleteGroup = useCallback(
    (groupId: string) => {
      // Save group and affected participants for undo
      const group = currentEvent?.groups.find((g) => g.id === groupId);
      const affectedParticipantIds = currentEvent?.participants
        .filter((p) => p.groupId === groupId)
        .map((p) => p.id) || [];

      updateCurrentEvent((event) => ({
        ...event,
        groups: event.groups.filter((g) => g.id !== groupId),
        participants: event.participants.map((p) =>
          p.groupId === groupId ? { ...p, groupId: "" } : p
        ),
      }));

      if (group) {
        toast("Grupo eliminado", {
          action: {
            label: "Deshacer",
            onClick: () => {
              updateCurrentEvent((event) => ({
                ...event,
                groups: [...event.groups, group],
                participants: event.participants.map((p) =>
                  affectedParticipantIds.includes(p.id) ? { ...p, groupId } : p
                ),
              }));
            },
          },
          duration: 5000,
        });
      }
    },
    [currentEvent, updateCurrentEvent]
  );

  const assignParticipantToGroup = useCallback(
    (participantId: string, groupId: string) => {
      updateCurrentEvent((event) => {
        // Remove participant from current group and handle representative reassignment
        let updatedGroups = event.groups.map((group) => {
          const newParticipantIds = group.participantIds.filter(
            (id) => id !== participantId
          );

          // If this participant was the representative, assign a new one
          let newRepresentativeId = group.representativeId;
          if (group.representativeId === participantId) {
            newRepresentativeId = newParticipantIds[0] || "";
          }

          return {
            ...group,
            participantIds: newParticipantIds,
            representativeId: newRepresentativeId,
          };
        });

        // Add to new group if groupId is provided
        if (groupId) {
          updatedGroups = updatedGroups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  participantIds: [...group.participantIds, participantId],
                }
              : group
          );
        }

        // Auto-delete empty groups
        const emptyGroups = updatedGroups.filter(
          (g) => g.participantIds.length === 0
        );
        updatedGroups = updatedGroups.filter(
          (g) => g.participantIds.length > 0
        );

        // Show toast for auto-deleted groups
        if (emptyGroups.length > 0) {
          emptyGroups.forEach((g) => {
            toast(`Grupo "${g.name}" eliminado automáticamente`);
          });
        }

        return {
          ...event,
          groups: updatedGroups,
          participants: event.participants.map((p) =>
            p.id === participantId ? { ...p, groupId } : p
          ),
        };
      });
    },
    [updateCurrentEvent]
  );

  // Expense operations
  const addExpense = useCallback(
    (
      description: string,
      amount: number,
      paidById: string,
      participantIds: string[]
    ): Expense | null => {
      if (!currentEventId) return null;

      const newExpense: Expense = {
        id: generateId(),
        description,
        amount,
        paidById,
        participantIds,
        createdAt: new Date().toISOString(),
      };

      updateCurrentEvent((event) => ({
        ...event,
        expenses: [...event.expenses, newExpense],
      }));

      haptics.success();
      return newExpense;
    },
    [currentEventId, updateCurrentEvent]
  );

  const updateExpense = useCallback(
    (
      expenseId: string,
      data: Partial<
        Pick<Expense, "description" | "amount" | "paidById" | "participantIds">
      >
    ) => {
      updateCurrentEvent((event) => ({
        ...event,
        expenses: event.expenses.map((e) =>
          e.id === expenseId ? { ...e, ...data } : e
        ),
      }));
    },
    [updateCurrentEvent]
  );

  const deleteExpense = useCallback(
    (expenseId: string) => {
      // Save expense for undo
      const expense = currentEvent?.expenses.find((e) => e.id === expenseId);

      updateCurrentEvent((event) => ({
        ...event,
        expenses: event.expenses.filter((e) => e.id !== expenseId),
      }));

      if (expense) {
        toast("Gasto eliminado", {
          action: {
            label: "Deshacer",
            onClick: () => {
              updateCurrentEvent((event) => ({
                ...event,
                expenses: [...event.expenses, expense],
              }));
            },
          },
          duration: 5000,
        });
      }
    },
    [currentEvent, updateCurrentEvent]
  );

  const value: EventContextValue = {
    events,
    currentEvent,
    currentEventId,
    isLoading,
    saveStatus,
    createEvent,
    updateEvent,
    deleteEvent,
    setCurrentEventId,
    addParticipant,
    updateParticipant,
    deleteParticipant,
    createGroup,
    updateGroup,
    deleteGroup,
    assignParticipantToGroup,
    addExpense,
    updateExpense,
    deleteExpense,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
}

export function useEvents() {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error("useEvents must be used within an EventProvider");
  }
  return context;
}
